import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Send, Loader2, MessageCircle, Mic, Square, Play, Pause, Trash2 } from 'lucide-react';
import { formatTime, formatDateTime } from '../lib/dateFormat';

const formatDay = (iso) => {
  try {
    const d = new Date(iso);
    const today = new Date();
    const diff = (today - d) / 86400000;
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (diff < 2) return 'Yesterday';
    return formatDateTime(d);
  } catch {
    return '';
  }
};

const fmtDuration = (ms) => {
  if (!ms || ms <= 0) return '0:00';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
};

/**
 * Inline audio player for a voice message bubble.
 * Has play/pause control + a progress indicator + duration label.
 */
const VoiceBubble = ({ src, durationMs, mine, isDark }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    const onTime = () => {
      const d = el.duration || (durationMs ? durationMs / 1000 : 0);
      setProgress(d > 0 ? Math.min(1, el.currentTime / d) : 0);
    };
    const onEnd = () => { setPlaying(false); setProgress(0); el.currentTime = 0; };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onEnd);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('ended', onEnd);
    };
  }, [durationMs]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  const trackBg = mine ? 'bg-white/30' : isDark ? 'bg-white/15' : 'bg-neutral-300';
  const trackFill = mine ? 'bg-white' : isDark ? 'bg-emerald-300' : 'bg-[#0078D4]';
  const btnCls = mine
    ? 'bg-white/20 hover:bg-white/30 text-white'
    : isDark
      ? 'bg-white/15 hover:bg-white/20 text-white'
      : 'bg-[#0078D4] hover:bg-[#005A9E] text-white';

  return (
    <div className="flex items-center gap-2.5 min-w-[180px]">
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        type="button"
        onClick={toggle}
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition ${btnCls}`}
        aria-label={playing ? 'Pause voice message' : 'Play voice message'}
        data-testid="voice-play-btn"
      >
        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
      </button>
      <div className="flex-1">
        <div className={`h-1.5 rounded-full overflow-hidden ${trackBg}`}>
          <div
            className={`h-full transition-[width] ${trackFill}`}
            style={{ width: `${progress * 100}%` }}
          />
        </div>
        <div className={`text-[10px] mt-1 ${mine ? 'text-white/80' : isDark ? 'text-slate-300' : 'text-neutral-500'}`}>
          🎤 Voice · {fmtDuration(durationMs)}
        </div>
      </div>
    </div>
  );
};

/**
 * OrderChat — chat thread between admin and a driver for a specific order.
 *
 * Props:
 *   apiBase: full URL ending in /api
 *   orderId: order id
 *   role: 'admin' | 'driver'
 *   authHeaders: bearer headers
 *   variant: 'light' (admin) | 'dark' (driver)
 */
const OrderChat = ({ apiBase, orderId, role, authHeaders, variant = 'light' }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  // Voice recording state
  const [recording, setRecording] = useState(false);
  const [recordingMs, setRecordingMs] = useState(0);
  const [voiceError, setVoiceError] = useState('');
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordStartedAtRef = useRef(0);
  const recordTickerRef = useRef(null);
  const recordStreamRef = useRef(null);

  const path = role === 'driver' ? 'driver/orders' : 'admin/orders';

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${apiBase}/${path}/${orderId}/messages`, { headers: authHeaders });
      setMessages(data || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [apiBase, path, orderId, authHeaders]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/${path}/${orderId}/messages`,
        { text: t },
        { headers: authHeaders },
      );
      setMessages((m) => [...m, data]);
      setText('');
    } catch (err) {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  const stopTracks = () => {
    if (recordStreamRef.current) {
      recordStreamRef.current.getTracks().forEach((t) => t.stop());
      recordStreamRef.current = null;
    }
    if (recordTickerRef.current) {
      clearInterval(recordTickerRef.current);
      recordTickerRef.current = null;
    }
  };

  const startRecording = async () => {
    setVoiceError('');
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setVoiceError('Voice recording is not supported in this browser');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordStreamRef.current = stream;
      audioChunksRef.current = [];
      // Pick the best supported mime — webm/opus is widely supported on mobile + desktop.
      const mime =
        (window.MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/webm;codecs=opus') && 'audio/webm;codecs=opus') ||
        (window.MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/mp4') && 'audio/mp4') ||
        'audio/webm';
      const mr = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data);
      };
      mr.start();
      recordStartedAtRef.current = Date.now();
      setRecording(true);
      setRecordingMs(0);
      recordTickerRef.current = setInterval(() => {
        setRecordingMs(Date.now() - recordStartedAtRef.current);
        // Hard stop at 90s to keep clip under the 1MB server limit comfortably
        if (Date.now() - recordStartedAtRef.current > 90_000) {
          stopRecordingAndSend();
        }
      }, 200);
    } catch (e) {
      setVoiceError(e?.message || 'Microphone permission denied');
    }
  };

  const cancelRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      try { mr.stop(); } catch (e) { /* ignore */ }
    }
    audioChunksRef.current = [];
    stopTracks();
    setRecording(false);
    setRecordingMs(0);
  };

  const stopRecordingAndSend = async () => {
    const mr = mediaRecorderRef.current;
    if (!mr) return;
    const finalDuration = Date.now() - recordStartedAtRef.current;
    stopTracks();
    setRecording(false);

    // Wait for the final blob via onstop
    const blob = await new Promise((resolve) => {
      mr.onstop = () => {
        const b = new Blob(audioChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        resolve(b);
      };
      try { mr.stop(); } catch (e) { resolve(null); }
    });

    if (!blob || blob.size < 200) {
      setVoiceError('Voice message too short');
      setRecordingMs(0);
      return;
    }

    setBusy(true);
    try {
      const fd = new FormData();
      // Choose an extension that matches the mime for nicer filenames
      const ext = (blob.type || 'audio/webm').includes('mp4') ? 'm4a' : 'webm';
      fd.append('audio', blob, `voice-${Date.now()}.${ext}`);
      fd.append('duration_ms', String(finalDuration));
      const { data } = await axios.post(
        `${apiBase}/${path}/${orderId}/messages/voice`,
        fd,
        { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } },
      );
      setMessages((m) => [...m, data]);
    } catch (err) {
      setVoiceError(err?.response?.data?.detail || 'Could not send voice message');
    } finally {
      setBusy(false);
      setRecordingMs(0);
    }
  };

  const isDark = variant === 'dark';
  const wrapCls = isDark
    ? 'bg-[#0B1220] border border-white/10 text-white'
    : 'bg-white border border-neutral-200';

  const meBubble = isDark ? 'bg-emerald-500 text-white' : 'bg-[#0078D4] text-white';
  const themBubble = isDark ? 'bg-white/10 text-white' : 'bg-neutral-100 text-[#0F172A]';

  // Group messages by day
  const grouped = [];
  let prevDay = '';
  messages.forEach((m) => {
    const day = formatDay(m.created_at);
    if (day !== prevDay) {
      grouped.push({ type: 'sep', day });
      prevDay = day;
    }
    grouped.push({ type: 'msg', ...m });
  });

  return (
    <div className={`rounded-2xl overflow-hidden ${wrapCls} flex flex-col`} data-testid={`order-chat-${orderId}`}>
      <div className={`flex items-center gap-2 px-4 py-3 ${isDark ? 'border-b border-white/5' : 'border-b border-neutral-100'}`}>
        <MessageCircle className={`w-4 h-4 ${isDark ? 'text-emerald-300' : 'text-[#0078D4]'}`} />
        <h3 className="font-display text-sm font-bold">
          {role === 'admin' ? 'Chat with driver' : 'Chat with dispatch'}
        </h3>
        <span className={`ml-auto text-[10px] ${isDark ? 'text-slate-500' : 'text-neutral-400'}`}>
          {messages.length} msg{messages.length === 1 ? '' : 's'}
        </span>
      </div>

      <div
        ref={scrollRef}
        className={`px-4 py-3 max-h-[280px] min-h-[120px] overflow-y-auto ${
          isDark ? '' : 'bg-neutral-50/50'
        }`}
      >
        {loading ? (
          <div className={`text-center text-xs py-8 ${isDark ? 'text-slate-400' : 'text-neutral-500'}`}>
            <Loader2 className="w-4 h-4 mx-auto animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className={`text-center text-xs py-8 ${isDark ? 'text-slate-500' : 'text-neutral-400'}`}>
            No messages yet. Say hi 👋
          </div>
        ) : (
          <ul className="space-y-2">
            {grouped.map((g, i) => {
              if (g.type === 'sep') {
                return (
                  <li key={`sep-${i}`} className="text-center my-2">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
                      isDark ? 'bg-white/5 text-slate-400' : 'bg-neutral-200/70 text-neutral-500'
                    }`}>
                      {g.day}
                    </span>
                  </li>
                );
              }
              const mine = g.from_role === role;
              const hasVoice = !!g.voice;
              return (
                <li key={g.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${
                      mine ? meBubble : themBubble
                    } ${mine ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}
                    data-testid={`chat-msg-${g.id}`}
                  >
                    {hasVoice ? (
                      <VoiceBubble src={g.voice} durationMs={g.voice_duration_ms || 0} mine={mine} isDark={isDark} />
                    ) : (
                      <div className="whitespace-pre-wrap break-words">{g.text}</div>
                    )}
                    <div
                      className={`text-[10px] mt-0.5 ${
                        mine ? 'text-white/70' : isDark ? 'text-slate-400' : 'text-neutral-400'
                      } text-right`}
                    >
                      {formatTime(g.created_at)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Recording bar (shown only while recording) */}
      {recording && (
        <div className={`flex items-center gap-2 px-3 py-2 ${isDark ? 'bg-emerald-500/10 border-t border-emerald-500/30' : 'bg-red-50 border-t border-red-200'}`}>
          <span className={`w-2 h-2 rounded-full bg-red-500 animate-pulse`} />
          <span className={`text-xs font-bold ${isDark ? 'text-emerald-200' : 'text-red-700'}`}>
            Recording · {fmtDuration(recordingMs)}
          </span>
          <span className={`ml-auto text-[10px] ${isDark ? 'text-slate-300' : 'text-neutral-500'}`}>Max 90s</span>
          <button
            type="button"
            onClick={cancelRecording}
            className={`h-7 px-2.5 rounded-lg text-[11px] font-semibold inline-flex items-center gap-1 ${isDark ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-white hover:bg-neutral-100 text-red-700 border border-red-200'}`}
            data-testid="voice-cancel-btn"
          >
            <Trash2 className="w-3 h-3" /> Cancel
          </button>
          <button
            type="button"
            onClick={stopRecordingAndSend}
            className="h-7 px-3 rounded-lg text-[11px] font-semibold inline-flex items-center gap-1 bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="voice-send-btn"
          >
            <Send className="w-3 h-3" /> Send
          </button>
        </div>
      )}

      {voiceError && !recording && (
        <div className={`text-[11px] px-3 py-1.5 ${isDark ? 'bg-red-500/10 text-red-300' : 'bg-red-50 text-red-700'}`}>
          {voiceError}
        </div>
      )}

      <form onSubmit={send} className={`flex items-end gap-2 px-3 py-3 ${isDark ? 'border-t border-white/5 bg-[#070B16]' : 'border-t border-neutral-100 bg-white'}`}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={1}
          placeholder="Type a message…"
          disabled={busy || recording}
          className={`flex-1 resize-none rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
            isDark
              ? 'bg-white/5 text-white placeholder:text-slate-500 focus:ring-emerald-400/40 border border-white/10'
              : 'bg-neutral-50 text-[#0F172A] placeholder:text-neutral-400 focus:ring-[#0078D4]/30 border border-neutral-200'
          }`}
          style={{ maxHeight: 110 }}
          data-testid={`chat-input-${orderId}`}
        />
        {/* Mic / Stop button */}
        {!text.trim() && (
          <button
            type="button"
            onClick={recording ? stopRecordingAndSend : startRecording}
            disabled={busy}
            className={`h-10 px-3.5 rounded-xl flex items-center justify-center transition ${
              recording
                ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse'
                : isDark
                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                  : 'bg-[#0078D4] hover:bg-[#005A9E] text-white'
            }`}
            data-testid={`chat-mic-${orderId}`}
            title={recording ? 'Stop & send' : 'Record voice message'}
          >
            {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
          </button>
        )}
        {/* Send (text) button — only shown when there's text */}
        {text.trim() && (
          <button
            type="submit"
            disabled={busy || !text.trim()}
            className={`h-10 px-3.5 rounded-xl flex items-center justify-center transition ${
              isDark
                ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                : 'bg-[#0078D4] hover:bg-[#005A9E] text-white'
            }`}
            data-testid={`chat-send-${orderId}`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        )}
      </form>
    </div>
  );
};

export default OrderChat;
